import React, { useLayoutEffect, useRef, useState } from "react";
import { Button, DatePicker, Form, Input, Select, TimePicker } from "antd";
import { Formik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";

import Modals from "../../common/modal";
import {
  convertToHHMM,
  convertYYYYToDayJS,
  formatDateToYYYYMMDD,
  formatToYYYY,
  ltrim,
} from "../../../util/helper";
import "../../../style/modules/routes.scss";
import { asyncSearchAndGetSupplier } from "../../../redux/slices/supplierSlice";
import { asyncSearchAndGetPlant } from "../../../redux/slices/plantSlice";
import DragAndDrop from "./dragAndDrop";
import { RouteFromSchema } from "../../../validators/formSchema";
import {
  asyncCreateRoute,
  asyncEditRoute,
  asyncGetRoute,
} from "../../../redux/slices/routeSlice";

const { Option } = Select;

const RouteModal = ({ openModal, setOpenModal, messageApi, modalData }) => {
  const formRef = useRef(null);
  const dispatch = useDispatch();
  const { loading, pagination, editLoading, suppliersData, plantsData } =
    useSelector((state) => ({
      loading: state.route.createRoute.loading,
      pagination: state.route.getRoute.pagination,
      editLoading: state.route.editRoute.loading,
      suppliersData: state.supplier.searchAndGetSupplier.data,
      plantsData: state.plant.searchAndGetPlant.data,
    }));

  const format = "HH:mm";
  const dateFormat = "YYYY-MM-DD";
  // const convertDate = "YYYY-M-D";

  const [isDirty, setIsDirty] = useState(false);

  const footerElem = [
    <div className="modal_footer">
      <Button
        key="submit"
        loading={modalData.mode === "add" ? loading : editLoading}
        disabled={modalData.mode === "edit" && !isDirty}
        onClick={() => {
          if (formRef.current) {
            formRef.current.handleSubmit();
          }
        }}
      >
        {modalData.mode === "add" ? "Submit" : "Update"}
      </Button>
      <Button key="back" onClick={() => setOpenModal(false)}>
        Cancel
      </Button>
    </div>,
  ];

  const templateDetails = {
    id: "",
    arriveTime: "",
    departTime: "",
    arriveDate: "",
    departDate: "",
  };

  const initialValue = {
    customer: "",
    puroute: "",
    routeDate: "",
    specMiles: "",
    rteType: "solo",
    relay: "Y",
    frequency: "5",
    period: "",
    periodYear: "",
    pudTime: "",
    suppliersIds: [{ ...templateDetails }],
    plant_id: "",
    plantArrivalTime: "",
    plantArrivalDate: "",
    plantDepartTime: "",
    plantDepartDate: "",
  };

  const editInitialValue = {
    id: modalData.data?.id || "",
    customer: modalData.data?.customer || "",
    puroute: modalData.data?.puroute || "",
    routeDate: modalData.data?.routeDate
      ? dayjs(modalData.data?.routeDate)
      : null,
    specMiles: modalData.data?.specMiles || "",
    rteType: modalData.data?.rteType || "",
    relay: modalData.data?.relay || "",
    frequency: modalData.data?.frequency || "",
    period: modalData.data?.period || "",
    periodYear: modalData.data?.periodYear
      ? convertYYYYToDayJS(modalData.data?.periodYear)
      : null,
    pudTime: modalData.data?.pudTime
      ? dayjs(modalData.data?.pudTime, "HH:mm")
      : null,
    suppliersIds: (modalData.data?.suppliersData || []).map((supplier) => ({
      id: supplier.id,
      arriveTime: supplier.arriveTime
        ? dayjs(supplier.arriveTime, "HH:mm")
        : null,
      departTime: supplier.departTime
        ? dayjs(supplier.departTime, "HH:mm")
        : null,
      arriveDate: supplier.arriveDate ? dayjs(supplier.arriveDate) : null,
      departDate: supplier.departDate ? dayjs(supplier.departDate) : null,
    })) || [{ ...templateDetails }],
    plant_id: modalData.data?.plant_id || "",
    plantArrivalTime: modalData.data?.plantArrivalTime
      ? dayjs(modalData.data?.plantArrivalTime, "HH:mm")
      : null,
    plantArrivalDate: modalData.data?.plantArrivalDate
      ? dayjs(modalData.data?.plantArrivalDate)
      : null,
    plantDepartTime: modalData.data?.plantDepartTime
      ? dayjs(modalData.data?.plantDepartTime, "HH:mm")
      : null,
    plantDepartDate: modalData.data?.plantDepartDate
      ? dayjs(modalData.data?.plantDepartDate)
      : null,
  };

  useLayoutEffect(() => {
    dispatch(asyncSearchAndGetSupplier());
    dispatch(asyncSearchAndGetPlant());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modals
      title={
        modalData.mode === "add"
          ? "Create New Route"
          : modalData.mode === "edit"
          ? "Update Route"
          : "View Route"
      }
      open={openModal}
      setOpen={setOpenModal}
      footer={modalData.mode === "view" ? false : footerElem}
      width={1000}
    >
      <div style={{ margin: "0 0 2rem 0" }}>
        <Formik
          innerRef={formRef}
          validationSchema={RouteFromSchema}
          initialValues={
            modalData?.mode === "add" ? initialValue : editInitialValue
          }
          onSubmit={(values) => {
            const newValue = {
              ...values,
              customer: values.customer?.trimEnd() || "",
              puroute: values.puroute?.trimEnd() || "",
              specMiles: values.specMiles?.trimEnd() || "",
              frequency: values.frequency?.trimEnd() || "",
              period: values.period?.trimEnd() || "",
              routeDate: formatDateToYYYYMMDD(values.routeDate),
              periodYear: formatToYYYY(values.periodYear),
              pudTime: convertToHHMM(values.pudTime),
              suppliersIds: values.suppliersIds.map((supplier, index) => ({
                ...supplier,
                order: index + 1,
                arriveTime: convertToHHMM(supplier.arriveTime),
                departTime: convertToHHMM(supplier.departTime),
                arriveDate: formatDateToYYYYMMDD(supplier.arriveDate),
                departDate: formatDateToYYYYMMDD(supplier.departDate),
              })),
              plantArrivalTime: convertToHHMM(values.plantArrivalTime),
              plantDepartTime: convertToHHMM(values.plantDepartTime),
              plantArrivalDate: formatDateToYYYYMMDD(values.plantArrivalDate),
              plantDepartDate: formatDateToYYYYMMDD(values.plantDepartDate),
            };

            if (modalData.mode === "add") {
              dispatch(asyncCreateRoute(newValue)).then((res) => {
                if (res.payload?.status === 201) {
                  messageApi.open({
                    type: "success",
                    content:
                      res.payload?.data?.message ||
                      "Route Created Successfully",
                  });
                  dispatch(
                    asyncGetRoute({
                      page: pagination.page,
                      limit: pagination.pageSize,
                    })
                  );
                  setOpenModal(false);
                } else {
                  messageApi.open({
                    type: "error",
                    content: res.payload?.message || "Something went wrong",
                  });
                }
              });
            } else {
              dispatch(asyncEditRoute(newValue)).then((res) => {
                if (res.payload?.status === 200) {
                  messageApi.open({
                    type: "success",
                    content:
                      res.payload?.data?.message ||
                      "Route Updated Successfully",
                  });
                  dispatch(
                    asyncGetRoute({
                      page: pagination.page,
                      limit: pagination.pageSize,
                    })
                  );
                  setOpenModal(false);
                } else {
                  messageApi.open({
                    type: "error",
                    content: res.payload?.message || "Something went wrong",
                  });
                }
              });
            }
          }}
        >
          {({
            handleChange,
            handleSubmit,
            setFieldValue,
            values,
            errors,
            touched,
            setDirty,
          }) => (
            <div className="custom-scrollbar form_div">
              <Form
                onFinish={(e) => {
                  setDirty(false);
                  handleSubmit(e);
                }}
              >
                <Form.Item style={{ margin: "0" }}>
                  <div className="route_input_front">
                    <div className="route_input_front_label">
                      <label
                        htmlFor="customer1"
                        className="form_label_front required"
                      >
                        Customer
                      </label>
                    </div>
                    <div className="route_input_front_content">
                      <Input
                        id="customer1"
                        type="text"
                        name="customer"
                        className="form-input"
                        placeholder="Enter customer"
                        onChange={(e) => {
                          if (handleChange) {
                            handleChange(e);
                            setIsDirty(true);
                          }
                        }}
                        value={values?.customer ? ltrim(values?.customer) : ""}
                        status={`${
                          (errors.customer && touched.customer && "error") || ""
                        }`}
                        disabled={modalData.mode === "view"}
                      />
                      {errors.customer && touched.customer && (
                        <small id="customer-help" className="error-message">
                          {errors.customer}
                        </small>
                      )}
                    </div>
                  </div>
                </Form.Item>

                <Form.Item style={{ margin: "0" }}>
                  <div className="route_input_front">
                    <div className="route_input_front_label">
                      <label
                        htmlFor="puroute1"
                        className="form_label_front required"
                      >
                        Puroute
                      </label>
                    </div>
                    <div className="route_input_front_content">
                      <Input
                        id="latitude1"
                        type="text"
                        name="puroute"
                        className="form-input"
                        placeholder="Enter puroute"
                        onChange={(e) => {
                          if (handleChange) {
                            handleChange(e);
                            setIsDirty(true);
                          }
                        }}
                        value={values?.puroute ? ltrim(values?.puroute) : ""}
                        status={`${
                          (errors.puroute && touched.puroute && "error") || ""
                        }`}
                        disabled={modalData.mode === "view"}
                      />
                      {errors.puroute && touched.puroute && (
                        <small id="puroute-help" className="error-message">
                          {errors.puroute}
                        </small>
                      )}
                    </div>
                  </div>
                </Form.Item>

                <Form.Item style={{ margin: "0" }}>
                  <div className="route_input_front">
                    <div className="route_input_front_label">
                      <label
                        htmlFor="routeDate1"
                        className="form_label_front required"
                      >
                        Route Date
                      </label>
                    </div>
                    <div className="route_input_front_content">
                      <DatePicker
                        format={dateFormat}
                        style={{ height: "2.7rem", width: "100%" }}
                        id="routeDate1"
                        onChange={(value) => {
                          setFieldValue("routeDate", value);
                          setIsDirty(true);
                        }}
                        value={values.routeDate}
                        placeholder="YYYY-MM-DD"
                        status={`${
                          (errors.routeDate && touched.routeDate && "error") ||
                          ""
                        }`}
                        disabled={modalData.mode === "view"}
                      />
                      {errors.routeDate && touched.routeDate && (
                        <small id="routeDate-help" className="error-message">
                          {errors.routeDate}
                        </small>
                      )}
                    </div>
                  </div>
                </Form.Item>

                <Form.Item style={{ margin: "0" }}>
                  <div className="route_input_front">
                    <div className="route_input_front_label">
                      <label
                        htmlFor="specMiles1"
                        className="form-label required"
                      >
                        Spec Miles
                      </label>
                    </div>
                    <div className="route_input_front_content">
                      <Input
                        id="specMiles1"
                        type="text"
                        name="specMiles"
                        className="form-input"
                        placeholder="Enter spec miles"
                        onChange={(e) => {
                          if (handleChange) {
                            handleChange(e);
                            setIsDirty(true);
                          }
                        }}
                        value={
                          values?.specMiles ? ltrim(values?.specMiles) : ""
                        }
                        status={`${
                          (errors.specMiles && touched.specMiles && "error") ||
                          ""
                        }`}
                        disabled={modalData.mode === "view"}
                      />
                      {errors.specMiles && touched.specMiles && (
                        <small id="specMiles-help" className="error-message">
                          {errors.specMiles}
                        </small>
                      )}
                    </div>
                  </div>
                </Form.Item>

                <Form.Item style={{ margin: "0" }}>
                  <div className="route_input_front">
                    <div className="route_input_front_label">
                      <label htmlFor="rteType1" className="form-label required">
                        RTE Type
                      </label>
                    </div>
                    <div className="route_input_front_content">
                      <Select
                        id="rteType1"
                        style={{ height: "2.7rem" }}
                        placeholder="Select a RTE type"
                        onChange={(value) => {
                          setFieldValue("rteType", value);
                          setIsDirty(true);
                        }}
                        value={values?.rteType}
                        showSearch
                        allowClear
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          option?.children
                            ?.toLowerCase()
                            ?.indexOf(input?.toLowerCase()) >= 0
                        }
                        status={`${
                          (errors.rteType && touched.rteType && "error") || ""
                        }`}
                        disabled={modalData.mode === "view"}
                      >
                        <Option value="solo">Solo</Option>
                        <Option value="team">Team</Option>
                        <Option value="hybrid">Hybrid</Option>
                      </Select>
                      {errors.rteType && touched.rteType && (
                        <small id="rteType-help" className="error-message">
                          {errors.rteType}
                        </small>
                      )}
                    </div>
                  </div>
                </Form.Item>

                <Form.Item style={{ margin: "0" }}>
                  <div className="route_input_front">
                    <div className="route_input_front_label">
                      <label htmlFor="relay1" className="form-label required">
                        Relay
                      </label>
                    </div>
                    <div className="route_input_front_content">
                      <Select
                        id="relay1"
                        style={{ height: "2.7rem" }}
                        placeholder="Select a relay"
                        onChange={(value) => {
                          setFieldValue("relay", value);
                          setIsDirty(true);
                        }}
                        value={values?.relay}
                        showSearch
                        allowClear
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          option?.children
                            ?.toLowerCase()
                            ?.indexOf(input?.toLowerCase()) >= 0
                        }
                        status={`${
                          (errors.relay && touched.relay && "error") || ""
                        }`}
                        disabled={modalData.mode === "view"}
                      >
                        <Option value="Y">Y</Option>
                        <Option value="N">N</Option>
                      </Select>
                      {errors.relay && touched.relay && (
                        <small id="relay-help" className="error-message">
                          {errors.relay}
                        </small>
                      )}
                    </div>
                  </div>
                </Form.Item>

                <Form.Item style={{ margin: "0" }}>
                  <div className="route_input_front">
                    <div className="route_input_front_label">
                      <label
                        htmlFor="frequency1"
                        className="form-label required"
                      >
                        Frequency
                      </label>
                    </div>
                    <div className="route_input_front_content">
                      <Input
                        id="frequency1"
                        type="text"
                        name="frequency"
                        className="form-input"
                        placeholder="Enter frequency"
                        onChange={(e) => {
                          if (handleChange) {
                            handleChange(e);
                            setIsDirty(true);
                          }
                        }}
                        value={
                          values?.frequency ? ltrim(values?.frequency) : ""
                        }
                        status={`${
                          (errors.frequency && touched.frequency && "error") ||
                          ""
                        }`}
                        disabled={modalData.mode === "view"}
                      />
                      {errors.frequency && touched.frequency && (
                        <small id="frequency-help" className="error-message">
                          {errors.frequency}
                        </small>
                      )}
                    </div>
                  </div>
                </Form.Item>

                {/* <Form.Item style={{ margin: "0" }}>
                  <div className="route_input_front">
                    <div className="route_input_front_label">
                      <label
                        htmlFor="departDay1"
                        className="form-label required"
                      >
                        Depart Day
                      </label>
                    </div>
                    <div className="route_input_front_content">
                      <Input
                        id="departDay1"
                        type="text"
                        name="departDay"
                        className="form-input"
                        placeholder="Enter depart day"
                        onChange={(e) => {
                          if (handleChange) {
                            handleChange(e);
                            setIsDirty(true);
                          }
                        }}
                        value={
                          values?.departDay ? ltrim(values?.departDay) : ""
                        }
                        status={`${
                          (errors.departDay && touched.departDay && "error") ||
                          ""
                        }`}
                        disabled={modalData.mode === "view"}
                      />
                      {errors.departDay && touched.departDay && (
                        <small id="departDay-help" className="error-message">
                          {errors.departDay}
                        </small>
                      )}
                    </div>
                  </div>
                </Form.Item> */}

                <Form.Item style={{ margin: "0" }}>
                  <div className="route_input_front">
                    <div className="route_input_front_label">
                      <label htmlFor="period1" className="form-label required">
                        Period
                      </label>
                    </div>
                    <div className="route_input_front_content">
                      <Input
                        id="period1"
                        type="text"
                        name="period"
                        className="form-input"
                        placeholder="Enter period"
                        onChange={(e) => {
                          if (handleChange) {
                            handleChange(e);
                            setIsDirty(true);
                          }
                        }}
                        value={values?.period ? ltrim(values?.period) : ""}
                        status={`${
                          (errors.period && touched.period && "error") || ""
                        }`}
                        disabled={modalData.mode === "view"}
                      />
                      {errors.period && touched.period && (
                        <small id="period-help" className="error-message">
                          {errors.period}
                        </small>
                      )}
                    </div>
                  </div>
                </Form.Item>

                <Form.Item style={{ margin: "0" }}>
                  <div className="route_input_front">
                    <div className="route_input_front_label">
                      <label
                        htmlFor="periodYear1"
                        className="form-label required"
                      >
                        Period Year
                      </label>
                    </div>
                    <div className="route_input_front_content">
                      <DatePicker
                        style={{ height: "2.7rem", width: "100%" }}
                        id="periodYear1"
                        picker="year"
                        onChange={(value) => {
                          setFieldValue("periodYear", value);
                          setIsDirty(true);
                        }}
                        value={values.periodYear}
                        placeholder="YYYY"
                        status={`${
                          (errors.periodYear &&
                            touched.periodYear &&
                            "error") ||
                          ""
                        }`}
                        disabled={modalData.mode === "view"}
                      />
                      {errors.periodYear && touched.periodYear && (
                        <small id="periodYear-help" className="error-message">
                          {errors.periodYear}
                        </small>
                      )}
                    </div>
                  </div>
                </Form.Item>

                <Form.Item style={{ margin: "0" }}>
                  <div className="route_input_front">
                    <div className="route_input_front_label">
                      <label htmlFor="pudTime1" className="form-label required">
                        PUD Time
                      </label>
                    </div>
                    <div className="route_input_front_content">
                      <TimePicker
                        style={{ height: "2.7rem", width: "100%" }}
                        id="pudTime1"
                        format={format}
                        onChange={(value) => {
                          setFieldValue("pudTime", value);
                          setIsDirty(true);
                        }}
                        value={values.pudTime}
                        placeholder="HH:MM"
                        status={`${
                          (errors.pudTime && touched.pudTime && "error") || ""
                        }`}
                        disabled={modalData.mode === "view"}
                      />
                      {errors.pudTime && touched.pudTime && (
                        <small id="pudTime -help" className="error-message">
                          {errors.pudTime}
                        </small>
                      )}
                    </div>
                  </div>
                </Form.Item>

                <DragAndDrop
                  values={values}
                  errors={errors}
                  touched={touched}
                  setFieldValue={setFieldValue}
                  suppliersData={suppliersData}
                  templateDetails={templateDetails}
                  mode={modalData?.mode}
                  setIsDirty={setIsDirty}
                  dateFormat={dateFormat}
                />

                <div className="route_input_front">
                  <div
                    className="route_plant_front_label"
                    style={{ marginTop: "2rem" }}
                  >
                    <label
                      htmlFor="plant1"
                      className="form_label_front required"
                    >
                      Plant
                    </label>
                  </div>
                  <div className="route_select_plant_content">
                    <Form.Item style={{ margin: "0" }}>
                      <label htmlFor="plant1" className="form-label required">
                        Select Plant
                      </label>
                      <Select
                        id="plant1"
                        style={{ height: "2.7rem" }}
                        placeholder="Select a plant"
                        onChange={(value) => {
                          setFieldValue("plant_id", value);
                          setIsDirty(true);
                        }}
                        value={values?.plant_id}
                        showSearch
                        allowClear
                        optionFilterProp="children"
                        // filterOption={(input, option) =>
                        //   option?.children
                        //     ?.toLowerCase()
                        //     ?.indexOf(input?.toLowerCase()) >= 0
                        // }
                        status={`${
                          (errors.plant_id && touched.plant_id && "error") || ""
                        }`}
                        disabled={modalData.mode === "view"}
                      >
                        {plantsData?.map((option) => (
                          <Option key={option.id} value={option.id}>
                            {option.code} - {option.plantName}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                    {errors.plant_id && touched.plant_id && (
                      <small id="plant_id-help" className="error-message">
                        {errors.plant_id}
                      </small>
                    )}
                  </div>

                  <div className="route_plant_front_content">
                    <Form.Item style={{ margin: "0" }}>
                      <label
                        htmlFor="plantArrivalDate1"
                        className="form-label required"
                      >
                        Arrival Date
                      </label>
                      <DatePicker
                        format={dateFormat}
                        style={{ height: "2.7rem", width: "100%" }}
                        id="plantArrivalDate1"
                        onChange={(value) => {
                          setFieldValue("plantArrivalDate", value);
                          setIsDirty(true);
                        }}
                        value={values.plantArrivalDate}
                        placeholder="YYYY-MM-DD"
                        status={`${
                          (errors.plantArrivalDate &&
                            touched.plantArrivalDate &&
                            "error") ||
                          ""
                        }`}
                        disabled={modalData.mode === "view"}
                      />
                    </Form.Item>
                    {errors.plantArrivalDate && touched.plantArrivalDate && (
                      <small
                        id="plantArrivalDate-help"
                        className="error-message"
                      >
                        {errors.plantArrivalDate}
                      </small>
                    )}
                  </div>
                  <div className="route_plant_front_content">
                    <Form.Item style={{ margin: "0" }}>
                      <label
                        htmlFor="plantArrivalTime1"
                        className="form-label required"
                      >
                        Arrival Time
                      </label>
                      <TimePicker
                        style={{ height: "2.8rem" }}
                        id="plantArrivalTime1"
                        format={format}
                        onChange={(value) => {
                          setFieldValue("plantArrivalTime", value);
                          setIsDirty(true);
                        }}
                        value={values.plantArrivalTime}
                        placeholder="HH:MM"
                        status={`${
                          (errors.plantArrivalTime &&
                            touched.plantArrivalTime &&
                            "error") ||
                          ""
                        }`}
                        disabled={modalData.mode === "view"}
                      />
                    </Form.Item>
                    {errors.plantArrivalTime && touched.plantArrivalTime && (
                      <small
                        id="plantArrivalTime-help"
                        className="error-message"
                      >
                        {errors.plantArrivalTime}
                      </small>
                    )}
                  </div>
                  <div className="route_plant_front_content">
                    <Form.Item style={{ margin: "0" }}>
                      <label
                        htmlFor="plantDepartDate1"
                        className="form-label required"
                      >
                        Depart Date
                      </label>
                      <DatePicker
                        format={dateFormat}
                        style={{ height: "2.7rem", width: "100%" }}
                        id="plantDepartDate1"
                        onChange={(value) => {
                          setFieldValue("plantDepartDate", value);
                          setIsDirty(true);
                        }}
                        value={values.plantDepartDate}
                        placeholder="YYYY-MM-DD"
                        status={`${
                          (errors.plantDepartDate &&
                            touched.plantDepartDate &&
                            "error") ||
                          ""
                        }`}
                        disabled={modalData.mode === "view"}
                      />
                    </Form.Item>
                    {errors.plantDepartDate && touched.plantDepartDate && (
                      <small
                        id="plantDepartDate-help"
                        className="error-message"
                      >
                        {errors.plantDepartDate}
                      </small>
                    )}
                  </div>
                  <div className="route_plant_front_content">
                    <Form.Item style={{ margin: "0" }}>
                      <label
                        htmlFor="plantDepartTime1"
                        className="form-label required"
                      >
                        Depart Time
                      </label>
                      <TimePicker
                        style={{ height: "2.8rem" }}
                        id="plantDepartTime1"
                        format={format}
                        onChange={(value) => {
                          setFieldValue("plantDepartTime", value);
                          setIsDirty(true);
                        }}
                        value={values.plantDepartTime}
                        placeholder="HH:MM"
                        status={`${
                          (errors.plantDepartTime &&
                            touched.plantDepartTime &&
                            "error") ||
                          ""
                        }`}
                        disabled={modalData.mode === "view"}
                      />
                    </Form.Item>
                    {errors.plantDepartTime && touched.plantDepartTime && (
                      <small
                        id="plantDepartTime-help"
                        className="error-message"
                      >
                        {errors.plantDepartTime}
                      </small>
                    )}
                  </div>
                </div>
              </Form>
            </div>
          )}
        </Formik>
      </div>
    </Modals>
  );
};

export default RouteModal;
